import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2, Eye, EyeOff, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { API } from '../App';

/** Blog CRUD. Admin writes markdown; published posts appear at /blog. */
export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', body_markdown: '', excerpt: '', cover_image: '', author_name: '', is_published: true });
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/blog`); setPosts(r.data.posts || []); }
    catch { toast.error('Could not load posts'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body_markdown.trim()) { toast.error('Title and body are required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/blog`, form);
      toast.success('Post created');
      setForm({ title: '', body_markdown: '', excerpt: '', cover_image: '', author_name: '', is_published: true });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSubmitting(false); }
  };

  const togglePublish = async (id, published) => {
    try { await axios.put(`${API}/admin/blog/${id}/publish?published=${!published}`); reload(); }
    catch { toast.error('Could not update'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try { await axios.delete(`${API}/admin/blog/${id}`); toast.success('Deleted'); reload(); }
    catch { toast.error('Could not delete'); }
  };

  return (
    <div className="space-y-6" data-testid="blog-admin">
      <div>
        <h2 className="text-2xl font-heading font-medium">Blog</h2>
        <p className="text-sm text-muted-foreground mt-1">Markdown supported (# h1, ## h2, ### h3, **bold**, *italic*, - bullet).</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Plus className="h-4 w-4" /> New Post</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="blog-title" />
            <Textarea placeholder="Body (markdown)" value={form.body_markdown} onChange={(e) => setForm({ ...form, body_markdown: e.target.value })} rows={8} required data-testid="blog-body" />
            <Input placeholder="Excerpt (optional, first 200 chars used if blank)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="blog-excerpt" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input placeholder="Cover image URL (optional)" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="blog-cover" />
              <Input placeholder="Author name (optional)" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} data-testid="blog-author" />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
              Publish immediately
            </label>
            <Button type="submit" disabled={submitting} data-testid="blog-submit">{submitting ? 'Saving…' : 'Save Post'}</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Posts ({posts.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
            : posts.length === 0 ? <p className="text-sm text-muted-foreground">No posts yet.</p>
            : (
              <ul className="divide-y divide-border/40">
                {posts.map((p) => (
                  <li key={p.post_id} className="py-3 flex items-start gap-3" data-testid={`blog-row-${p.post_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-foreground text-sm">{p.title}</span>
                        <Badge variant={p.is_published ? 'default' : 'secondary'} className="text-xs">{p.is_published ? 'Published' : 'Draft'}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">/blog/{p.slug} • {p.author_name}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => togglePublish(p.post_id, p.is_published)} data-testid={`blog-toggle-${p.post_id}`}>
                      {p.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => remove(p.post_id)} data-testid={`blog-delete-${p.post_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
